import { IBatch, ICreate, IGet, IReportList, IUpdate, update } from '../../actions/admin/data';
import {
  cloneRequest,
  copyRequest,
  createRequest,
  getRequest,
  listRequest,
  moveRequest,
  updateRequest,
  reportRequest,
  getResponsesRequest,
  resetRequest,
  batchRequest,
} from '../../services/admin';
import {
  actionRequest,
  attemptRequest,
  certificateRequest,
  completeRequest,
  dialogueRequest,
  groupPermissionRequest,
  homeRequest,
  moduleRequest,
  searchRequest,
  systemPermissionRequest,
  tokenRequest,
  verificationRequest,
} from '../../services/product';
import { LoadingState, SavingState } from 'types/domain/base';
import {
  FulfilledActionFromAsyncThunk,
  AnyAsyncThunk,
  RejectedActionFromAsyncThunk,
  PendingActionFromAsyncThunk,
} from '@reduxjs/toolkit/dist/matchers';
import merge, { mergeCreate } from './utils/merge';
import {
  IActionAction,
  IAttemptAction,
  ICertificateResponse,
  IGroupPermissionResponse,
  IModuleAction,
  IPlayerResponse,
  ITokenAction,
  ITokenActionResponse,
  IVerificationAction,
} from 'types/action/player';
import { EntityTypes } from 'constants/entities';
import { copy, mergeCopyOffline } from './utils/copy';
import { IMove, IReset, move } from 'core/actions/admin/diagram';
import { ISessionAttempt } from 'types/domain/session';
import { IModuleDAO } from 'types/domain/module';
import { hydrate } from 'core/actions/product/state';
import { ISearch, ISearchResponse, SearchType } from 'types/domain/search';
import { ITextSearchDAO } from 'types/domain/text';
import { IOrganisationUserDAO } from 'types/domain/user';
import { IReport, ReportType } from 'core/slices/report';
import { uploadActions } from 'core/slices/upload';
import { IEntityReducer } from '.';
import { domainRequest, systemSigninRequest } from 'core/services/authentication';

export function cases(builder) {
  builder
    .addCase(hydrate, (state, action) => {
      if (action.payload.prodEnt)
        Object.keys(action.payload.prodEnt).map(type => {
          if (state[type]) state[type] = { ...state[type], ...action.payload.prodEnt[type] };
        });
    })
    .addCase(update, (state, action) => {
      const { entity, type, parentEntityAttribute, parentEntityType, parentEntityId, mergeData } =
        action.payload as IUpdate;

      //return a new state to syncronise undos
      if (!state[type]) return { ...state };

      if (mergeData) merge(state, type, entity.id, entity);
      else {
        const entities = get(state, type);
        entities[entity.id] = {
          ...(entities[entity.id] || {}),
          ...entity,
        };
        state[type] = entities;
      }

      if (parentEntityAttribute && state[parentEntityType])
        merge(state, parentEntityType, parentEntityId, { id: parentEntityId, [parentEntityAttribute]: entity.id });
    })
    .addCase(updateRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entity } = action.meta.arg as IUpdate;
      //updateSaving(state, type, entity.id, SavingState.SAVING, null);
    })

    .addCase(updateRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entity } = action.meta.arg as IUpdate;
      updateSaving(state, type, entity?.id, SavingState.ERROR, action.error.message);
    })
    .addCase(updateRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entity, mergeData } = action.meta.arg as IUpdate;
      //updateSaving(state, type, entity.id, SavingState.SAVED, null);
      if (mergeData) merge(state, type, entity.id, action.payload);
    })
    .addCase(batchRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { batch } = action.meta.arg as IBatch;
      batch.forEach(update => {
        merge(state, update.type, update.entity.id, update.entity);

        const { parentEntityAttribute, parentEntityType, parentEntityId } = update;

        if (parentEntityAttribute && state[parentEntityType])
          merge(state, parentEntityType, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: update.entity.id,
          });
      });
    })
    .addCase(batchRequest.fulfilled, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { batch } = action.meta.arg as IBatch;

      batch.forEach((update, i) => {
        const entity = action.payload[i];

        if (!entity?.id) return;

        merge(state, update.type, entity.id, entity);

        const { parentEntityAttribute, parentEntityType, parentEntityId } = update;

        if (parentEntityAttribute)
          merge(state, parentEntityType, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: entity.id,
          });
      });
    })
    .addCase(getRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(getRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(getRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADED, null);
      merge(state, type, entityId, action.payload);
    })
    .addCase(reportRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(reportRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(reportRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, reportType } = action.meta.arg as IReportList;
      updateLoading(state, type, null, LoadingState.LOADED, null);
      merge(state, type, null, action.payload.list);
    })
    .addCase(getResponsesRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(getResponsesRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(getResponsesRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADED, null);
      merge(state, type, entityId, action.payload);
    })
    .addCase(listRequest.pending, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type } = action.meta.arg as IGet;
      updateLoading(state, type, null, LoadingState.LOADING, null);
    })
    .addCase(listRequest.rejected, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type } = action.meta.arg as IGet;
      updateLoading(state, type, null, LoadingState.ERROR, action.error.message);
    })
    .addCase(listRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type } = action.meta.arg as IGet;
      updateLoading(state, type, null, LoadingState.LOADED, null);
      merge(state, type, null, action.payload.list);
    })
    .addCase(createRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, entity, parentEntityType, parentEntityAttribute, parentEntityId } = action.meta.arg as ICreate;
      merge(state, type, entity.offlineId, { id: entity.offlineId, ...entity });

      if (parentEntityAttribute)
        merge(state, parentEntityType, parentEntityId, { id: parentEntityId, [parentEntityAttribute]: entity.id });

      //  if(type === EntityTypes.ACTION)
    })
    .addCase(createRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, offlineId } = action.meta.arg;
      updateSaving(state, type, offlineId, SavingState.ERROR, action.error.message);
    })
    .addCase(createRequest.fulfilled, mergeCreate)
    .addCase(cloneRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.LOADING, null);
    })
    .addCase(cloneRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(cloneRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id, error } = action.meta.arg;
      merge(state, type, action.payload.id, action.payload);
    })
    .addCase(copyRequest.pending, copy)
    .addCase(copyRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(copyRequest.fulfilled, mergeCopyOffline)
    .addCase(moveRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { items, offset, parentEntityId, parentEntityType } = action.meta.arg as IMove;

      if (parentEntityType === EntityTypes.MODULE)
        items.forEach(item => {
          if (state[item.type][item.id]) state[item.type][item.id].moduleId = parentEntityId;
        });
      else
        items.forEach(item => {
          if (state[item.type][item.id] && item.order) state[item.type][item.id].order = item.order;
        });

      // merge(state, EntityTypes.NODE, null, items);
    })
    .addCase(moveRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(moveRequest.fulfilled, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { items, parentEntityId, parentEntityType } = action.meta.arg as IMove;
      const { nodes } = action.payload;

      if (nodes) merge(state, EntityTypes.NODE, null, nodes);
    })
    .addCase(resetRequest.pending, (state: IEntityReducer, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { ids, parentEntityId, parentEntityType } = action.meta.arg as IReset;

      if (parentEntityType === EntityTypes.MODULE) {
        Object.values(state[EntityTypes.ACTION])
          .filter(a => a.moduleId === parentEntityId)
          .forEach(a => {
            state[EntityTypes.ACTION][a.id].nodeId = null;
          });
      } else if (parentEntityType === EntityTypes.COURSE) {
        Object.values(state[EntityTypes.COURSE_NODE])
          .filter(a => a.courseId === parentEntityId)
          .forEach(a => {
            state[EntityTypes.COURSE_NODE][a.id].nodeId = null;
          });
      }
    })
    .addCase(resetRequest.rejected, (state, action: RejectedActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(tokenRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      if (!action.payload) return;

      const { orgConfig } = action.payload as ITokenActionResponse;
      const { token, tokenCreated } = action.meta.arg as ITokenAction;
      if (orgConfig) merge(state, EntityTypes.ORG_CONFIG, orgConfig.id, orgConfig);
      mergePlayerResponses(state, action.payload.playerResponse);
    })
    .addCase(systemSigninRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      if (!action.payload) return;

      const { orgConfig } = action.payload as ITokenActionResponse;
      const { token, tokenCreated } = action.meta.arg as ITokenAction;
      if (orgConfig) merge(state, EntityTypes.ORG_CONFIG, orgConfig.id, orgConfig);
      mergePlayerResponses(state, action.payload.playerResponse);
    })
    .addCase(groupPermissionRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { permission } = action.payload as IGroupPermissionResponse;

      if (permission) merge(state, EntityTypes.COURSE_PROGRESS, permission.id, permission);
    })
    .addCase(verificationRequest.pending, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(state, EntityTypes.VERIFICATION_REQUEST, verificationRequestId, SavingState.SAVING, null);
    })
    .addCase(verificationRequest.rejected, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(
        state,
        EntityTypes.VERIFICATION_REQUEST,
        verificationRequestId,
        SavingState.ERROR,
        action.error.message,
      );
    })
    .addCase(verificationRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(state, EntityTypes.VERIFICATION_REQUEST, verificationRequestId, SavingState.SAVED, null);
      mergePlayerResponses(state, action.payload);
    })
    .addCase(systemPermissionRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      mergePlayerResponses(state, action.payload);
    })
    .addCase(actionRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { token, tokenCreated, actionId } = action.meta.arg as IActionAction;
      mergePlayerResponses(state, action.payload);
    })
    .addCase(completeRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { token, tokenCreated, actionId } = action.meta.arg as IActionAction;
      mergePlayerResponses(state, action.payload);
    })
    .addCase(moduleRequest.pending, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { id } = action.meta.arg as IModuleAction;
      updateLoading(state, EntityTypes.MODULE, id, LoadingState.LOADING, null);
    })
    .addCase(moduleRequest.rejected, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { id } = action.meta.arg as IModuleAction;
      updateLoading(state, EntityTypes.MODULE, id, LoadingState.ERROR, action.error);
    })
    .addCase(moduleRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const module = action.payload as IModuleDAO;

      merge(state, EntityTypes.MODULE, module.id, { ...module, loading: LoadingState.LOADED, error: null });
    })
    .addCase(attemptRequest.pending, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { offlineId, courseProgressId } = action.meta.arg as IAttemptAction;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, courseProgressId, SavingState.SAVING, null);
      //merge(state, EntityTypes.ACCESS_PERMISSION, courseProgressId, { sessionAttemptId: offlineId });
    })
    .addCase(attemptRequest.rejected, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { courseProgressId } = action.meta.arg as IAttemptAction;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, courseProgressId, SavingState.ERROR, action.error.message);
    })
    .addCase(attemptRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const sessionAttempt = action.payload as ISessionAttempt;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, sessionAttempt.id, SavingState.SAVED, null);
      merge(state, EntityTypes.SESSION_ATTEMPT, sessionAttempt.id, sessionAttempt);
    })
    .addCase(dialogueRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      mergePlayerResponses(state, action.payload);
    })
    .addCase(homeRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      merge(state, EntityTypes.COURSE_NODE, null, action.payload);
    })
    .addCase(uploadActions.uploadProcessing, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      if (action.payload.type === EntityTypes.SCENE)
        merge(state, EntityTypes.SCENE, action.payload.entityId, {
          id: action.payload.entityId,
          progress: action.payload.entity,
        });
    })
    .addCase(searchRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
      const { searchType } = action.meta.arg as ISearch;
      const searchResponse = action.payload as ISearchResponse;

      if (searchResponse.results?.list) {
        if ([SearchType.LEARNER, SearchType.MODULE].includes(searchType))
          merge(state, EntityTypes.TEXT, null, searchResponse.results.list);

        if (searchType === SearchType.ORG_USER) merge(state, EntityTypes.ORG_USER, null, searchResponse.results.list);
      }
    });

  // .addCase(certificateRequest.pending, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
  //   const { id } = action.meta.arg as ICertificateAction;
  //   updateLoading(state, EntityTypes.ORG_USER_CERTIFICATE, id, LoadingState.LOADING, null);
  // })
  // .addCase(certificateRequest.rejected, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
  //   const { id } = action.meta.arg as ICertificateAction;
  //   updateLoading(state, EntityTypes.ORG_USER_CERTIFICATE, id, LoadingState.ERROR, action.error);
  // })
  // .addCase(certificateRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
  //   const { certificate } = action.payload as ICertificateResponse;
  //   if (certificate) merge(state, EntityTypes.ORG_USER_CERTIFICATE, certificate.id, certificate);
  // })
  // .addCase(domainRequest.fulfilled, (state, action: FulfilledActionFromAsyncThunk<AnyAsyncThunk>) => {
  //   const { course } = action.payload
  //   //if (course) merge(state, EntityTypes.COURSE, course.id, course);
  // });

  return builder;
}

function mergePlayerResponses(state, payload) {
  if (!payload) return;

  const { queue } = payload as IPlayerResponse;
  queue.forEach(item => {
    if (item.verificationRequest)
      merge(state, EntityTypes.VERIFICATION_REQUEST, item.verificationRequest.id, item.verificationRequest);
    if (item.module) merge(state, EntityTypes.MODULE, item.module.id, item.module);
    if (item.dialogue) merge(state, EntityTypes.COURSE_NODE, item.dialogue.courseNode.id, item.dialogue.courseNode);
  });
}

function updateLoading(state, type, id, loading, error) {
  const entities = get(state, type);

  if (!state[type]) return state;

  id
    ? (entities[id] = {
        ...(entities[id] || {}),
        loading,
        error,
      })
    : (entities.loading = loading);

  state[type] = entities;
}

function updateSaving(state, type, id, saving, error) {
  const entities = get(state, type);

  if (!state[type]) return state;

  id
    ? (entities[id] = {
        ...(entities[id] || {}),
        saving,
        error,
      })
    : (entities.saving = saving);

  state[type] = entities;
}

export function get(state, type) {
  return { ...state[type] };
}
